import { Link } from 'react-router-dom';
import { useDocumentTitle } from '@/lib/hooks';
import styles from './StaticPage.module.css';

/**
 * Written to be read by an actual person in about ninety seconds, not by a
 * lawyer. Every claim on this page has to be true of the code — if the
 * behaviour ever changes, this page changes with it.
 */
export function PrivacyPage() {
  useDocumentTitle('Privacy — Are We Compatible?');

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <p className={styles.eyebrow}>Privacy</p>
        <h1 className={styles.title}>What this site does with your information</h1>
        <p className={styles.lede}>
          Short version: almost nothing, and nothing at all unless you decide to get
          in touch.
        </p>
      </header>

      <section className={styles.section}>
        <h2>While you are answering the questions</h2>
        <p>
          Your answers are saved in your own browser, on your own device, and the
          compatibility result is worked out there too. None of it is sent to a server.
          Closing the tab and coming back later works because the answers never left.
        </p>
        <p>
          There is a button on the result page that erases them. They also expire on
          their own after two weeks.
        </p>
      </section>

      <section className={styles.section}>
        <h2>If you choose to get in touch</h2>
        <p>
          The contact form shows you the exact list of what will be sent before you send
          it. That is:
        </p>
        <ul className={styles.list}>
          <li>Your first name</li>
          <li>The single contact detail you chose to give</li>
          <li>Your message, if you wrote one</li>
          <li>Which of the three results you got, if you did the questions</li>
          <li>
            Your answers. <strong>Only</strong> if you tick that box separately
          </li>
        </ul>
        <p>
          You can also get in touch without answering anything. Nothing about a
          result is sent in that case, because there is not one. James is simply
          told you wrote from the profile page.
        </p>
        <p>
          It goes to James. It is not shared with anyone else, not sold, not used to
          advertise anything to you, and it does not create an account or subscribe you
          to anything.
        </p>
      </section>

      <section className={styles.section}>
        <h2>What is not collected</h2>
        <ul className={styles.list}>
          <li>No cookies for advertising or tracking</li>
          <li>
            No device fingerprinting. Nothing about your screen, fonts, hardware
            or installed software is read
          </li>
          <li>No precise location</li>
          <li>No third-party trackers, pixels or embedded social widgets</li>
          <li>No profile built about you in the background</li>
        </ul>
      </section>

      <section className={styles.section}>
        <h2>Anonymous counts</h2>
        <p>
          The site counts things like &ldquo;a page was opened&rdquo;, &ldquo;the
          questions were started&rdquo;, &ldquo;question five was reached&rdquo; and
          &ldquo;a result was shown&rdquo;, so it is possible to tell whether anything
          is broken and whether the questions are worth answering. Those counts never
          include the content of an answer, anything you typed, or anything that
          identifies you.
        </p>
        <p>
          To tell one visit apart from another, two things are used, and neither is a
          name for you:
        </p>
        <ul className={styles.list}>
          <li>
            A random tag for your browser tab. It is thrown away the moment you close
            the tab, and it does not reach any other tab, device or day.
          </li>
          <li>
            A random code worked out from part of your network address. It cannot be
            turned back into that address, and the secret used to make it is held only
            in memory and rotated every day. Today&rsquo;s code and tomorrow&rsquo;s
            have nothing to do with each other, and last week&rsquo;s cannot be worked
            out again by anyone.
          </li>
        </ul>
        <p>
          The practical effect is that it is possible to know roughly how many people
          came on a given day and how far they got, and not possible to know that any
          of them was you, or that you came back.
        </p>
        <p>
          If your browser sends Global Privacy Control or Do Not Track, nothing is
          counted at all. No tag is created and no code is worked out.
        </p>
      </section>

      <section className={styles.section}>
        <h2>How long anything is kept</h2>
        <p>
          If you get in touch, what you sent is kept until you ask for it to be deleted.
          There is no fixed expiry date, and saying otherwise would be inventing a
          deadline nothing actually enforces.
        </p>
        <p>
          Ask at any time, including in the message itself, and it gets deleted.
        </p>
      </section>

      <p className={styles.back}>
        <Link to="/">Back to the start</Link>
      </p>
    </div>
  );
}
